import { useEffect, useState, useCallback, useMemo } from 'react';
import type { UserPresenceState } from '@annoteer/sync-layer';
import { useSync } from '../contexts/SyncContext.js';

/**
 * Core hook for managing user presence (cursors, current page, visibility).
 * Stale cursors (>5s without update) are automatically nulled out by AwarenessManager.
 */
export function useAwareness() {
  const { awareness } = useSync();
  const [remoteCursors, setRemoteCursors] = useState<Map<number, UserPresenceState>>(new Map());
  const [cursorVisible, setCursorVisible] = useState(true);

  useEffect(() => {
    if (!awareness) return;
    const unsubscribe = awareness.onPresenceChange((states) => {
      setRemoteCursors(new Map(states));
    });
    return unsubscribe;
  }, [awareness]);

  /** Flat array of remote users, derived from the cursors Map. */
  const remoteUsers = useMemo(() => Array.from(remoteCursors.values()), [remoteCursors]);

  /** Get remote cursors that are on a specific page (with non-null cursor position). */
  const getCursorsOnPage = useCallback(
    (pageNumber: number): UserPresenceState[] => {
      return remoteUsers.filter((u) => u.pointer !== null && u.currentPage === pageNumber);
    },
    [remoteUsers],
  );

  const updateCursor = useCallback(
    (x: number, y: number) => {
      if (!cursorVisible || !awareness) return;
      awareness.setCursorPosition(x, y);
    },
    [awareness, cursorVisible],
  );

  const clearCursor = useCallback(() => {
    awareness?.clearCursor();
  }, [awareness]);

  const setCurrentPage = useCallback(
    (page: number) => {
      awareness?.setCurrentPage(page);
    },
    [awareness],
  );

  const toggleCursorVisibility = useCallback(() => {
    setCursorVisible((prev) => {
      if (prev) awareness?.clearCursor();
      return !prev;
    });
  }, [awareness]);

  return {
    remoteCursors,
    remoteUsers,
    cursorVisible,
    updateCursor,
    clearCursor,
    setCurrentPage,
    toggleCursorVisibility,
    getCursorsOnPage,
  };
}
